import { useCallback } from 'react';

import useAnalytics from '../useAnalytics';

interface EventParameters {
  class?: string;
  subject?: string;
  level?: string;
  [key: string]: string | number | boolean | null | undefined;
}

const useSendEvents = () => {
  const { trackEvent } = useAnalytics();

  const programLoginStartEvent = useCallback(
    (programCode: string, category: string) => {
      trackEvent({
        name: 'program_login_start',
        label: programCode,
        category,
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const programLoginFailureEvent = useCallback(
    (programCode: string, category?: string) => {
      trackEvent({
        name: 'program_login_failure',
        label: programCode,
        category,
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const programLoginSuccessEvent = useCallback(
    (programCode: string, category: string) => {
      trackEvent({
        name: 'program_login_success',
        label: programCode,
        category,
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const diagnosticActivationAttemptedEvent = useCallback(
    (grade: string, subject: string) => {
      trackEvent({
        name: 'diagnostic_activation_attempted',
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  const diagnosticActivationFailedEvent = useCallback(
    (code: number, grade: string, subject: string) => {
      trackEvent({
        name: 'diagnostic_activation_failed',
        value: code,
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  const diagnosticStartedEvent = useCallback(
    (grade: string, subject: string) => {
      trackEvent({
        name: 'diagnostic_started',
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  const diagnosticResumedEvent = useCallback(
    (grade: string, subject: string) => {
      trackEvent({
        name: 'diagnostic_resumed',
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  const videoPlayedInstructionEvent = useCallback(
    (grade: string, subject: string) => {
      trackEvent({
        name: 'video_played',
        category: 'Diagnostic Instructions',
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  const diagnosticClickedEvent = useCallback(
    (grade: string, subject: string) => {
      trackEvent({
        name: 'diagnostic_clicked',
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  // Student Login Events
  const studentLoginStartEvent = useCallback(
    (grade: string, label: string) => {
      trackEvent({
        name: 'student_login_start',
        label,
        params: {
          class: grade,
        },
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const studentLoginSuccessEvent = useCallback(
    (grade: string) => {
      trackEvent({
        name: 'student_login_success',
        params: {
          class: grade,
        },
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const studentLoginFailureEvent = useCallback(
    (grade: string, label: string) => {
      trackEvent({
        name: 'student_login_failure',
        label,
        params: {
          class: grade,
        },
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const gradeClickedEvent = useCallback(
    (grade: string) => {
      trackEvent({
        name: 'grade_clicked',
        params: {
          class: grade,
        },
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const forgotPasswordClickedEvent = useCallback(
    (grade: string) => {
      trackEvent({
        name: 'forgot_password_clicked',
        params: {
          class: grade,
        },
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  // Subject and Profile Events
  const subjectClickedEvent = useCallback(
    (grade: string, subject: string) => {
      trackEvent({
        name: 'subject_clicked',
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  const studentProfileViewedEvent = useCallback(
    (grade: string) => {
      trackEvent({
        name: 'student_profile_viewed',
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const missionStartedEvent = useCallback(
    (label: string, value: number, parameters: EventParameters) => {
      trackEvent({
        name: 'mission_started',
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const missionPracticedEvent = useCallback(
    (label: string, parameters: EventParameters) => {
      trackEvent({
        name: 'mission_practiced',
        label,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const stageStartedEvent = useCallback(
    (category: string, label: string, value: number, parameters: EventParameters) => {
      trackEvent({
        name: 'stage_started',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const stageExitedEvent = useCallback(
    (category: string, label: string, value: string, parameters: EventParameters) => {
      trackEvent({
        name: 'stage_exited',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const missionCardCollectedEvent = useCallback(
    (label: string, grade: string) => {
      trackEvent({
        name: 'mission_card_collected',
        label,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  // Cueing and ES Events
  const cueingStartedEvent = useCallback(
    (category: string, label: string, parameters: EventParameters) => {
      trackEvent({
        name: 'cueing_started',
        category,
        label,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const esStartedEvent = useCallback(
    (category: string, label: string, parameters: EventParameters) => {
      trackEvent({
        name: 'es_started',
        category,
        label,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const cueingCompletedEvent = useCallback(
    (category: string, label: string, value: number, parameters: EventParameters) => {
      trackEvent({
        name: 'cueing_completed',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const esCompletedEvent = useCallback(
    (category: string, label: string, value: number, parameters: EventParameters) => {
      trackEvent({
        name: 'es_completed',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  // Mission and Stage Completion Events
  const missionCompletedEvent = useCallback(
    (category: string, label: string, value: number, parameters: EventParameters) => {
      trackEvent({
        name: 'mission_completed',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const stageCompletedEvent = useCallback(
    (category: string, label: string, value: number, parameters: EventParameters) => {
      trackEvent({
        name: 'stage_completed',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const stageClearedEvent = useCallback(
    (category: string, label: string, parameters: EventParameters) => {
      trackEvent({
        name: 'stage_cleared',
        category,
        label,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const missionClearedEvent = useCallback(
    (label: string, parameters: EventParameters) => {
      trackEvent({
        name: 'mission_cleared',
        label,
        params: parameters,
      });
    },
    [trackEvent],
  );

  // Question Events
  const questionAttemptedEvent = useCallback(
    (category: string, label: string, value: string, parameters: EventParameters) => {
      trackEvent({
        name: 'question_attempted',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const questionCorrectEvent = useCallback(
    (category: string, label: string, value: string, parameters: EventParameters) => {
      trackEvent({
        name: 'question_correct',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  const questionIncorrectEvent = useCallback(
    (category: string, label: string, value: string, parameters: EventParameters) => {
      trackEvent({
        name: 'question_incorrect',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  // Task Player Events
  const hintClickedEvent = useCallback(
    (category: string, label: string, value: number, parameters: EventParameters) => {
      trackEvent({
        name: 'hint_clicked',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  // Diagnostic Events (additional)
  const diagnosticAbortedEvent = useCallback(
    (grade: string, subject: string) => {
      trackEvent({
        name: 'diagnostic_aborted',
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  const diagnosticCompletedEvent = useCallback(
    (grade: string, subject: string, category: string) => {
      trackEvent({
        name: 'diagnostic_completed',
        category,
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  // Video Player Events
  const videoCompletedEvent = useCallback(
    (category: string, value: number, grade: string, subject: string) => {
      trackEvent({
        name: 'video_played',
        category,
        value,
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  // PDF Viewer Events
  const pdfWatchedEvent = useCallback(
    (category: string, value: number, grade: string, subject: string) => {
      trackEvent({
        name: 'pdf_watched',
        category,
        value,
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  // HTML Webview Events
  const htmlViewedEvent = useCallback(
    (category: string, value: number, grade: string, subject: string) => {
      trackEvent({
        name: 'html_viewed',
        category,
        value,
        params: {
          class: grade,
          subject,
        },
      });
    },
    [trackEvent],
  );

  // Level Events
  const levelCompletedEvent = useCallback(
    (grade: string, subject: string, level: string) => {
      trackEvent({
        name: 'level_completed',
        params: {
          class: grade,
          subject,
          level,
        },
      });
    },
    [trackEvent],
  );

  const levelFailedEvent = useCallback(
    (grade: string, subject: string, level: string) => {
      trackEvent({
        name: 'level_failed',
        params: {
          class: grade,
          subject,
          level,
        },
      });
    },
    [trackEvent],
  );

  // Stage Failed Event
  const stageFailedEvent = useCallback(
    (category: string, label: string, value: number, parameters: EventParameters) => {
      trackEvent({
        name: 'stage_failed',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  // Cueing Failed Event
  const cueingFailedEvent = useCallback(
    (category: string, label: string, value: string, parameters: EventParameters) => {
      trackEvent({
        name: 'cueing_failed',
        category,
        label,
        value,
        params: parameters,
      });
    },
    [trackEvent],
  );

  // Card Events
  const cardPageViewedEvent = useCallback(
    (label: string, grade: string) => {
      trackEvent({
        name: 'card_page_viewed',
        label,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const languageSelectedEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'language_selected',
        label,
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const locationSelectedEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'location_selected',
        label,
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const appResetEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'app_reset',
        label,
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const studentLogoutEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'student_log_out',
        label,
        sendToCleverTap: false,
      });
    },
    [trackEvent],
  );

  const ttsClickedEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'tts_clicked',
        label,
      });
    },
    [trackEvent],
  );

  const narrationClickedEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'narration_clicked',
        label,
      });
    },
    [trackEvent],
  );

  const narrationCompletedEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'narration_completed',
        label,
      });
    },
    [trackEvent],
  );

  const carrotEventEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'carrot_event',
        label,
      });
    },
    [trackEvent],
  );

  const enterFullScreenEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'enter_full_screen',
        label,
      });
    },
    [trackEvent],
  );

  const exitFullScreenEvent = useCallback(
    (label: string) => {
      trackEvent({
        name: 'exit_full_screen',
        label,
      });
    },
    [trackEvent],
  );

  const askSwifteeAppearedEvent = useCallback(
    (category: string, grade: string) => {
      trackEvent({
        name: 'ask_swiftee_appeared',
        category,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const askSwifteeClickedEvent = useCallback(
    (category: string, grade: string) => {
      trackEvent({
        name: 'ask_swiftee_clicked',
        category,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const swifteeChatWindowOpendedEvent = useCallback(
    (category: string, grade: string) => {
      trackEvent({
        name: 'swiftee_chat_window_opened',
        category,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const swifteeChatWindowClosedEvent = useCallback(
    (category: string, grade: string) => {
      trackEvent({
        name: 'swiftee_chat_window_closed',
        category,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const isDoubtSolvedShownEvent = useCallback(
    (category: string, grade: string) => {
      trackEvent({
        name: 'is_doubt_solved_shown',
        category,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const doubtSolvedResponseEvent = useCallback(
    (category: string, label: string, grade: string) => {
      trackEvent({
        name: 'doubt_solved_response',
        label,
        category,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const followUpQuestionShownEvent = useCallback(
    (category: string, grade: string) => {
      trackEvent({
        name: 'follow_up_question_shown',
        category,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const followUpQuestionAnsweredEvent = useCallback(
    (category: string, label: string, grade: string) => {
      trackEvent({
        name: 'follow_up_question_answered',
        category,
        label,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const feedbackScreenShownEvent = useCallback(
    (category: string, grade: string) => {
      trackEvent({
        name: 'feedback_screen_shown',
        category,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  const feedbackSelectedEvent = useCallback(
    (category: string, label: string, grade: string) => {
      trackEvent({
        name: 'feedback_selected',
        category,
        label,
        params: {
          class: grade,
        },
      });
    },
    [trackEvent],
  );

  // Course Mode Events
  const skillEntryClickedEvent = useCallback(
    (grade: string) => {
      trackEvent({
        name: 'skill_entry_clicked',
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const skillCourseClickedEvent = useCallback(
    (grade: string, category: string, label: string) => {
      trackEvent({
        name: 'skill_course_clicked',
        category,
        label,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const sessionClickedEvent = useCallback(
    (grade: string, moduleId: string, sessionId: string) => {
      trackEvent({
        name: 'session_clicked',
        category: moduleId,
        label: sessionId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const continueLearningClickedEvent = useCallback(
    (grade: string, moduleId: string, sessionId: string) => {
      trackEvent({
        name: 'continue_learning_clicked',
        category: moduleId,
        label: sessionId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const segmentStartedEvent = useCallback(
    (grade: string, segmentType: string, segmentId: string) => {
      trackEvent({
        name: 'segment_started',
        category: segmentType,
        label: segmentId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const segmentCompletedEvent = useCallback(
    (grade: string, segmentType: string, segmentId: string) => {
      trackEvent({
        name: 'segment_completed',
        category: segmentType,
        label: segmentId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const segmentDroppedEvent = useCallback(
    (grade: string, segmentType: string, segmentId: string) => {
      trackEvent({
        name: 'segment_dropped',
        category: segmentType,
        label: segmentId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const sessionCompletedEvent = useCallback(
    (grade: string, moduleId: string, sessionId: string) => {
      trackEvent({
        name: 'session_completed',
        category: moduleId,
        label: sessionId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const certificateViewedEvent = useCallback(
    (grade: string, source: string, certificateId: string) => {
      trackEvent({
        name: 'certificate_viewed',
        category: source,
        label: certificateId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const moreContentClickedEvent = useCallback(
    (grade: string, moduleId: string, sessionId: string) => {
      trackEvent({
        name: 'more_content_clicked',
        category: moduleId,
        label: sessionId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  const moreContentClosedEvent = useCallback(
    (grade: string, moduleId: string, sessionId: string) => {
      trackEvent({
        name: 'more_content_closed',
        category: moduleId,
        label: sessionId,
        params: { class: grade },
      });
    },
    [trackEvent],
  );

  return {
    programLoginStartEvent,
    programLoginFailureEvent,
    programLoginSuccessEvent,
    diagnosticActivationAttemptedEvent,
    diagnosticActivationFailedEvent,
    diagnosticStartedEvent,
    diagnosticResumedEvent,
    videoPlayedInstructionEvent,
    diagnosticClickedEvent,
    studentLoginStartEvent,
    studentLoginSuccessEvent,
    studentLoginFailureEvent,
    gradeClickedEvent,
    forgotPasswordClickedEvent,
    subjectClickedEvent,
    studentProfileViewedEvent,
    missionStartedEvent,
    missionPracticedEvent,
    stageStartedEvent,
    stageExitedEvent,
    missionCardCollectedEvent,
    cueingStartedEvent,
    esStartedEvent,
    cueingCompletedEvent,
    esCompletedEvent,
    missionCompletedEvent,
    stageCompletedEvent,
    stageClearedEvent,
    missionClearedEvent,
    questionAttemptedEvent,
    questionCorrectEvent,
    questionIncorrectEvent,
    hintClickedEvent,
    diagnosticAbortedEvent,
    diagnosticCompletedEvent,
    videoCompletedEvent,
    pdfWatchedEvent,
    htmlViewedEvent,
    levelCompletedEvent,
    levelFailedEvent,
    stageFailedEvent,
    cueingFailedEvent,
    cardPageViewedEvent,
    languageSelectedEvent,
    locationSelectedEvent,
    appResetEvent,
    studentLogoutEvent,
    ttsClickedEvent,
    narrationClickedEvent,
    narrationCompletedEvent,
    carrotEventEvent,
    enterFullScreenEvent,
    exitFullScreenEvent,
    askSwifteeAppearedEvent,
    askSwifteeClickedEvent,
    swifteeChatWindowOpendedEvent,
    isDoubtSolvedShownEvent,
    doubtSolvedResponseEvent,
    followUpQuestionShownEvent,
    followUpQuestionAnsweredEvent,
    feedbackScreenShownEvent,
    feedbackSelectedEvent,
    swifteeChatWindowClosedEvent,
    skillEntryClickedEvent,
    skillCourseClickedEvent,
    sessionClickedEvent,
    continueLearningClickedEvent,
    segmentStartedEvent,
    segmentCompletedEvent,
    segmentDroppedEvent,
    sessionCompletedEvent,
    certificateViewedEvent,
    moreContentClickedEvent,
    moreContentClosedEvent,
  };
};

export default useSendEvents;
